import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChevronDown, Pencil, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableFooter, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { CpmBreakdown, DisplayFee } from '@/services/earnings/fees'
import { formatCurrencyAmount, formatMoneyRange } from '../utils/money'
import { formatNumber } from '../utils/format'

/** Turns a fee provider enum — NORDIC_DATA_RESOURCES — into readable text. */
function humanise(name: string): string {
  return name
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * What to call a fee. Fees the seller set carry their own name; the ones the
 * platform derives are named here, since their server-side name is an internal
 * key (an audience provider, say) rather than something to show an advertiser.
 */
export function useFeeLabel() {
  const { t } = useTranslation()

  return function feeLabel(fee: DisplayFee): string {
    switch (fee.source) {
      case 'TECH':
        return t('fees.techFee')
      case 'CREATIVE_FORMAT':
        return t('fees.creativeFormatFee')
      case 'AUDIENCE':
        return fee.name
          ? t('fees.audienceFeeFor', { name: humanise(fee.name) })
          : t('fees.audienceFee')
      case 'SUPPLEMENTARY':
        return fee.name || t('fees.supplementaryFee')
      default:
        return fee.name || t(`fees.types.${fee.type}`)
    }
  }
}

/** A fee's charge, as an amount (or amount range) or a percentage. */
export function feeAmountText(fee: DisplayFee): string {
  if (fee.type === 'PERCENTAGE') {
    return `${formatNumber(fee.percentage ?? 0, { maximumFractionDigits: 2 })}%`
  }
  if (!fee.monetary) return '—'
  return formatMoneyRange(fee.monetary.amount, fee.maxAmount, fee.monetary.currency)
}

/** One line of the arithmetic: what it is on the left, what it costs on the right. */
function BreakdownRow({ label, value }: { label: string; value: string }) {
  return (
    <TableRow>
      <TableCell className="py-1 pl-0 text-xs text-muted-foreground">{label}</TableCell>
      <TableCell className="py-1 pr-0 text-right text-xs tabular-nums">{value}</TableCell>
    </TableRow>
  )
}

/** True when the breakdown has nothing to say — both pieces then render nothing. */
function isEmpty(breakdown: CpmBreakdown, hideWhenUnfeed: boolean): boolean {
  return !breakdown.grossCpm || (!breakdown.fees.length && hideWhenUnfeed)
}

interface BreakdownProps {
  breakdown: CpmBreakdown
  /** Hide the whole block when no fees are charged — the default. */
  hideWhenUnfeed?: boolean
  /**
   * Offers editing the fees: from the foot of the dropdown, or — while there
   * are none to break down — as a link of its own under the input.
   */
  onEditFees?: () => void
}

/**
 * The net CPM — the bid that actually enters the auction — as a summary line to
 * sit inside the gross CPM's own `.p-field`, under the input, so it reads as a
 * consequence of what was typed rather than a field of its own. The arithmetic
 * behind it opens in a dropdown, where a long list of fees costs the form no
 * height.
 *
 * When the fees swallow the whole bid the amount turns red on the line itself,
 * so the problem shows without anything being opened.
 */
export function CpmFeeBreakdown({
  breakdown,
  hideWhenUnfeed = true,
  onEditFees,
}: BreakdownProps) {
  const { t } = useTranslation()
  const feeLabel = useFeeLabel()
  const [open, setOpen] = useState(false)
  const { grossCpm, fees, netCpm, netCpmMax, feesExceedGross } = breakdown

  if (onEditFees && !fees.length) {
    return (
      <div className="mt-1">
        <Button
          type="button"
          variant="link"
          size="xs"
          className="h-auto px-0 text-muted-foreground hover:text-foreground"
          onClick={onEditFees}
        >
          <Plus />
          {t('fees.addFee')}
        </Button>
      </div>
    )
  }
  // Fees with no gross CPM yet still show, so they can be reached to edit.
  if (!onEditFees && isEmpty(breakdown, hideWhenUnfeed)) return null

  const netText = netCpm ? formatMoneyRange(netCpm.amount, netCpmMax, netCpm.currency) : '—'

  return (
    <div className="mt-1 flex items-center gap-2 text-xs">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help text-muted-foreground">
            {t('fees.netCpm')} · {t('fees.feeCount', { count: fees.length })}
          </span>
        </TooltipTrigger>
        <TooltipContent>{t('fees.netCpmHelp')}</TooltipContent>
      </Tooltip>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="ml-auto flex cursor-pointer items-center gap-1 font-semibold tabular-nums"
          >
            <span className={cn(feesExceedGross && 'text-(--ad-badge-red)')}>{netText}</span>
            <ChevronDown
              className={cn(
                'size-3.5 text-muted-foreground transition-transform',
                open && 'rotate-180'
              )}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-3">
          <Table>
            <TableBody>
              <BreakdownRow
                label={t('fees.grossCpm')}
                value={grossCpm ? formatCurrencyAmount(grossCpm) : '—'}
              />
              {fees.map((fee, i) => (
                <BreakdownRow
                  key={`${fee.source}-${i}`}
                  label={feeLabel(fee)}
                  value={`− ${feeAmountText(fee)}`}
                />
              ))}
            </TableBody>
            <TableFooter className="bg-transparent">
              <TableRow>
                <TableCell className="py-1 pl-0 text-xs">{t('fees.netCpm')}</TableCell>
                <TableCell
                  className={cn(
                    'py-1 pr-0 text-right text-xs font-semibold tabular-nums',
                    feesExceedGross && 'text-(--ad-badge-red)'
                  )}
                >
                  {netText}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
          {feesExceedGross && (
            <p className="mt-2 text-xs text-(--ad-badge-red)">{t('fees.feesExceedGross')}</p>
          )}
          {onEditFees && (
            <>
              <Separator className="my-2" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setOpen(false)
                  onEditFees()
                }}
              >
                <Pencil />
                {t('fees.editFees')}
              </Button>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
